import { StatusPalette } from '../constants/statusColors';

/**
 * Enum Definitions for aQRate API
 * All enums are string-based as per API v3.0
 */

/**
 * Submission Status Enum
 * @enum {string}
 */
export const SubmissionStatus = {
  DRAFT: 'Draft',
  IN_PROGRESS: 'InProgress',
  SUBMITTED: 'Submitted',
  UNDER_REVIEW: 'UnderReview',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ARCHIVED: 'Archived',
};

/**
 * Get submission status label (same as value for string enums)
 * @param {string} status - Status value
 * @returns {string} Status label
 */
export const getSubmissionStatusLabel = (status) => {
  // For string enums, we can add spaces between camelCase words
  const labels = {
    [SubmissionStatus.DRAFT]: 'Draft',
    [SubmissionStatus.IN_PROGRESS]: 'In Progress',
    [SubmissionStatus.SUBMITTED]: 'Submitted',
    [SubmissionStatus.UNDER_REVIEW]: 'Under Review',
    [SubmissionStatus.APPROVED]: 'Approved',
    [SubmissionStatus.REJECTED]: 'Rejected',
    [SubmissionStatus.ARCHIVED]: 'Archived',
  };
  return labels[status] || status;
};

/**
 * Get status color for UI display
 * @param {string} status - Status value
 * @returns {string} Color code
 */
export const getSubmissionStatusColor = (status) => {
  const colors = {
    [SubmissionStatus.DRAFT]: StatusPalette.slate,
    [SubmissionStatus.IN_PROGRESS]: StatusPalette.cyan,
    [SubmissionStatus.SUBMITTED]: StatusPalette.blue,
    [SubmissionStatus.UNDER_REVIEW]: StatusPalette.amber,
    [SubmissionStatus.APPROVED]: StatusPalette.green,
    [SubmissionStatus.REJECTED]: StatusPalette.red,
    [SubmissionStatus.ARCHIVED]: StatusPalette.gray,
  };
  return colors[status] || StatusPalette.slate;
};

/**
 * Permission Enum
 * @enum {string}
 */
export const Permission = {
  NONE: 'None',
  VIEW_FORMS: 'ViewForms',
  ADD_FORMS: 'AddForms',
  EDIT_FORMS: 'EditForms',
  DELETE_FORMS: 'DeleteForms',
  ALL_FORM_PERMISSIONS: 'AllFormPermissions',
  VIEW_SUBMISSIONS: 'ViewSubmissions',
  ADD_SUBMISSIONS: 'AddSubmissions',
  EDIT_SUBMISSIONS: 'EditSubmissions',
  DELETE_SUBMISSIONS: 'DeleteSubmissions',
  ALL_SUBMISSION_PERMISSIONS: 'AllSubmissionPermissions',
  ALL_PERMISSIONS: 'AllPermissions',
};

/**
 * Get permission label
 * @param {string} permission - Permission value
 * @returns {string} Permission label
 */
export const getPermissionLabel = (permission) => {
  const labels = {
    [Permission.NONE]: 'None',
    [Permission.VIEW_FORMS]: 'View Forms',
    [Permission.ADD_FORMS]: 'Add Forms',
    [Permission.EDIT_FORMS]: 'Edit Forms',
    [Permission.DELETE_FORMS]: 'Delete Forms',
    [Permission.ALL_FORM_PERMISSIONS]: 'All Form Permissions',
    [Permission.VIEW_SUBMISSIONS]: 'View Submissions',
    [Permission.ADD_SUBMISSIONS]: 'Add Submissions',
    [Permission.EDIT_SUBMISSIONS]: 'Edit Submissions',
    [Permission.DELETE_SUBMISSIONS]: 'Delete Submissions',
    [Permission.ALL_SUBMISSION_PERMISSIONS]: 'All Submission Permissions',
    [Permission.ALL_PERMISSIONS]: 'All Permissions',
  };
  return labels[permission] || permission;
};

/**
 * User Role Enum
 * @enum {string}
 */
export const UserRole = {
  SUPER_ADMIN: 'SuperAdmin',
  ORGANIZATION_ADMIN: 'OrganizationAdmin',
  USER: 'User',
};

/**
 * Get user role label
 * @param {string} role - Role value
 * @returns {string} Role label
 */
export const getUserRoleLabel = (role) => {
  const labels = {
    [UserRole.SUPER_ADMIN]: 'Super Admin',
    [UserRole.ORGANIZATION_ADMIN]: 'Organization Admin',
    [UserRole.USER]: 'User',
  };
  return labels[role] || role;
};

/**
 * Field Type Enum
 * @enum {string}
 */
export const FieldType = {
  TEXT: 'Text',
  TEXT_AREA: 'TextArea',
  EMAIL: 'Email',
  PHONE: 'Phone',
  URL: 'Url',
  NUMBER: 'Number',
  CURRENCY: 'Currency',
  PERCENTAGE: 'Percentage',
  SELECT: 'Select',
  RADIO: 'Radio',
  CHECKBOX: 'Checkbox',
  CHECKBOX_GROUP: 'CheckboxGroup',
  TOGGLE: 'Toggle',
  TAGS: 'Tags',
  DATE: 'Date',
  TIME: 'Time',
  DATE_TIME: 'DateTime',
  IMAGE: 'Image',
  DRAWING: 'Drawing',
  VOICE: 'Voice',
  FILE: 'File',
  LOCATION: 'Location',
  DIVIDER: 'Divider',
  HEADER: 'Header',
  SPACER: 'Spacer',
  SIGNATURE: 'Signature',
  RATING: 'Rating',
  SLIDER: 'Slider',
  SUBFORM: 'Subform',
  QR_CODE: 'QRCode',
  BARCODE: 'Barcode',
  REPEATED_FORM: 'RepeatedForm',
};

